import { useCallback, useEffect, useMemo, useState } from 'react';

import { useManualQuery, useMutation } from '@zenbusiness/application-commons-graphql';
import usePrevious from '@zenbusiness/application-commons-hooks/usePrevious';
import isUndefinedOrNull from '@zenbusiness/application-commons-utils/isUndefinedOrNull';
import Box from '@zenbusiness/zui/Box';
import Checkbox from '@zenbusiness/zui/Checkbox';
import CircularProgress from '@zenbusiness/zui/CircularProgress';
import FormControlLabel from '@zenbusiness/zui/FormControlLabel';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Radio from '@zenbusiness/zui/Radio';
import RadioGroup from '@zenbusiness/zui/RadioGroup';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import TextField from '@zenbusiness/zui/TextField';
import PropTypes from 'prop-types';
import { useNavigate, generatePath } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import ConfirmDeletePayeeModal from '#client/components/ConfirmDeletePayeeModal';
import GoogleMapField from '#client/components/GoogleMapField';
import { ActionButtonsGrid } from '#client/components/Grid';
import { TOKEN_TYPES } from '#client/constants/tokens';
import useBankingAccounts from '#client/hooks/useBankingAccounts';
import useBankingPath from '#client/hooks/useBankingPath';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useConst from '#client/hooks/useConst';
import useCustomerToken from '#client/hooks/useCustomerToken';
import useModalContext from '#client/hooks/useModalContext';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccount from '#client/hooks/useUnitAccount';
import useUnitAccountLimits from '#client/hooks/useUnitAccountLimits';
import { EDIT_PAYEE_MUTATION } from '#client/mutations';
import { INSTITUTION_QUERY } from '#client/queries';
import { formatNumber } from '#client/utils/currency';
import getLastSegmentUrl from '#client/utils/getLastSegmentUrl';

import {
  FieldContainerStyled,
  GroupTextInputStyled,
  WrapContentStyled,
  WrapInstitutionNameStyled
} from '../AddCounterPartyModal.styles';

import { EDIT_PAYEE_ENTRY_PAGES } from '../constants/entryPages';
import { FORM_FIELD_NAME } from '../constants/fields';
import { MAX_LENGTH_ROUTING_NUMBER } from '../constants/length';
import {
  BANK_NOT_SUPPORT_ACH_MESSAGE,
  NOT_SUPPORT_WIRE_MESSAGE,
  ROUTING_NUMBER_CURRENT_ERROR_MESSAGE,
  ROUTING_NUMBER_ERROR_MESSAGE
} from '../constants/messages';
import { PAYEE_ACCOUNT_TYPES, PAYEE_TYPES } from '../constants/types';
import { trimObjValues } from '../utils/stringUtils';
import {
  checkValidFields,
  confirmRoutingValidator,
  validateAccountErrorMessage
} from '../utils/validators';

const checkIcon = (
  <SharedAsset
    alt=""
    src="/s2/icon/check-circle-fill.svg"
  />
);
const validIcon = (
  <SharedAsset
    alt=""
    src="/icon/micro/check-circle-green.svg"
  />
);
const warningIcon = (
  <SharedAsset
    alt=""
    src="/icon/micro/warning.svg"
  />
);

function EditPayee({ handleClose, counterPartyInfo, editPayeeCallback, deletePayeeCallback }) {
  const navigate = useNavigate();
  const {
    sizes: { isDesktop }
  } = useBreakpoints();
  const { unitAccount } = useUnitAccount();
  const { getCustomerToken } = useCustomerToken({ type: TOKEN_TYPES.COMMON });
  const { loading: loadingLimit, wireOutgoingFee } = useUnitAccountLimits();
  const { id: bankingAccountId, loading: isFetchingBankingAccounts } = useBankingAccounts();

  const wireOutgoingFeeText = `$${formatNumber(wireOutgoingFee)}`;

  const [editPayee, { loading: loadingEditPayee }] = useMutation(EDIT_PAYEE_MUTATION, {
    noThrow: true
  });
  const idempotencyKey = useConst(uuidv4);
  const { createSuccessToast, createErrorToast } = useToastContext();
  const { openModal } = useModalContext();

  const {
    id: counterPartyId,
    type: counterPartyType,
    name: counterPartyName,
    accountType: counterPartyAccountType,
    firstName: counterPartyFirstName,
    lastName: counterPartyLastName,
    nickname: counterPartyNickname,
    fullAccountNumber: counterPartyAccountNumber,
    fullRoutingNumber: counterPartyRoutingNumber,
    isWireSupported,
    isACHSupported
  } = counterPartyInfo;

  const [isSelectWire, setSelectWire] = useState(isWireSupported || false);
  const [isSelectACH, setSelectACH] = useState(isACHSupported || false);
  const [wireAddressInput, setWireAddressInput] = useState('');
  const [formDataPayee, setFormDataPayee] = useState({
    type: counterPartyType,
    name: counterPartyType === PAYEE_TYPES.BUSINESS ? counterPartyName : '',
    firstName: counterPartyFirstName || '',
    lastName: counterPartyLastName || '',
    nickname: counterPartyNickname || '',
    accountType: counterPartyAccountType,
    accountNumber: counterPartyAccountNumber,
    confirmAccountNumber: counterPartyAccountNumber,
    routingNumber: counterPartyRoutingNumber,
    institutionName: '',
    wireRoutingNumber:
      isSelectWire && !isSelectACH
        ? counterPartyRoutingNumber
        : counterPartyInfo.wire.routingNumber || '',
    wireInstitutionName: ''
  });
  const [isNotChangeFormDataPayee, setIsNotChangeFormDataPayee] = useState(true);

  const [formErrorsPayee, setFormErrorsPayee] = useState({
    accountNumberError: null,
    routingNumberError: null,
    confirmAccountNumberError: null,
    isAccountNumberMatched: true,
    wireRoutingNumberError: null
  });
  const [wireAddress, setWireAddress] = useState({});

  const previousRoutingNumber = usePrevious(formDataPayee.routingNumber);
  const previousAccountNumber = usePrevious(formDataPayee.accountNumber);

  const { wireRoutingNumberError } = formErrorsPayee;

  const [getInstitution, { loading: loadingInstitution }] = useManualQuery(INSTITUTION_QUERY, {
    noThrow: true
  });

  const [getWireInstitution, { loading: loadingWireInstitution }] = useManualQuery(
    INSTITUTION_QUERY,
    {
      noThrow: true
    }
  );

  const bankingPath = useBankingPath();

  useEffect(() => {
    if (counterPartyInfo.wire.address) {
      const { city, country, postalCode, state, street } = counterPartyInfo.wire.address;
      setWireAddress({
        valid: true,
        address: {
          addressLine1: street,
          city,
          state,
          zip: postalCode,
          country
        }
      });
    }
  }, [counterPartyInfo]);

  useEffect(() => {
    const fetchInstitutionData = async () => {
      if (counterPartyRoutingNumber) {
        const { data } = await getInstitution({
          variables: {
            params: {
              routingNumber: counterPartyRoutingNumber
            }
          },
          skipCache: true
        });
        if (isSelectWire && !isSelectACH) {
          setFormDataPayee((prevData) => ({
            ...prevData,
            wireInstitutionName: data?.institution?.name
          }));
        }
        setFormDataPayee((prevData) => ({
          ...prevData,
          institutionName: data?.institution?.name
        }));
      }
    };

    fetchInstitutionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counterPartyRoutingNumber, getInstitution]);

  useEffect(() => {
    const fetchWireInstituationData = async () => {
      if (counterPartyInfo.wire.routingNumber) {
        const { data } = await getWireInstitution({
          variables: {
            params: {
              routingNumber: counterPartyInfo.wire.routingNumber
            }
          },
          skipCache: true
        });
        setFormDataPayee((prevData) => ({
          ...prevData,
          wireInstitutionName: data?.institution?.name
        }));
      }
    };
    fetchWireInstituationData();
  }, [counterPartyInfo, getWireInstitution]);

  /**
   * Handle Wire Transfer
   */
  const handleChangeWireRoutingNumber = useCallback((event) => {
    const { value } = event.target;
    setFormDataPayee((prevData) => ({
      ...prevData,
      wireRoutingNumber: value,
      wireInstitutionName: ''
    }));
    setIsNotChangeFormDataPayee(false);
  }, []);

  const handleBlurWireRoutingNumber = useCallback(async () => {
    const cloneFormData = trimObjValues({ ...formDataPayee });
    const { wireRoutingNumber } = cloneFormData;
    const errorMessage = validateAccountErrorMessage(
      wireRoutingNumber,
      FORM_FIELD_NAME.ROUTING_NUMBER
    );
    const shouldCallAPIVerifyRoutingNumber =
      !errorMessage &&
      wireRoutingNumber &&
      wireRoutingNumber.length >= 9 &&
      !loadingWireInstitution;
    if (shouldCallAPIVerifyRoutingNumber) {
      const { data: wireRoutingNumberdata, error } = await getWireInstitution({
        variables: {
          params: {
            routingNumber: wireRoutingNumber.trim()
          }
        },
        skipCache: true
      });

      // Should show error in some cases
      // 1. API has error : error ==> show error: ROUTING_NUMBER_ERROR_MESSAGE
      // 2. API has data but data.institution is null or undefined :
      // 3. API has data isWireSupported: false is not supported
      // case 1 and case 2 show error : ROUTING_NUMBER_ERROR_MESSAGE
      // case 3 show error : NOT_SUPPORT_WIRE_MESSAGE
      const shouldShowError =
        error ||
        isUndefinedOrNull(wireRoutingNumberdata?.institution) ||
        !wireRoutingNumberdata?.institution?.isWireSupported;

      if (shouldShowError) {
        setFormErrorsPayee((preErrors) => ({
          ...preErrors,
          wireRoutingNumberError:
            !isUndefinedOrNull(wireRoutingNumberdata?.institution) &&
            !wireRoutingNumberdata.institution.isWireSupported
              ? NOT_SUPPORT_WIRE_MESSAGE
              : ROUTING_NUMBER_ERROR_MESSAGE
        }));

        return;
      }

      // Success
      setFormDataPayee((prevData) => ({
        ...prevData,
        wireInstitutionName: wireRoutingNumberdata?.institution?.name
      }));
      setFormErrorsPayee((preErrors) => ({
        ...preErrors,
        wireRoutingNumberError: null
      }));

      return;
    }

    setFormErrorsPayee((preErrors) => ({
      ...preErrors,
      wireRoutingNumberError: errorMessage
    }));
    setFormDataPayee((prevData) => ({
      ...prevData,
      wireInstitutionName: ''
    }));
  }, [formDataPayee, getWireInstitution, loadingWireInstitution]);

  const inputInfoRender = useCallback(() => {
    const { wireInstitutionName } = formDataPayee;

    if (loadingWireInstitution) {
      return (
        <CircularProgress
          color="secondary"
          size={20}
        />
      );
    }

    if (!wireInstitutionName) {
      return null;
    }

    return (
      <Stack
        alignItems="center"
        direction="row"
      >
        <Stack
          alignItems="center"
          direction="row"
          spacing="4px"
        >
          <Text
            size="md"
            sx={{ width: 'max-content' }}
            weight="medium"
          >
            {wireInstitutionName}
          </Text>
          {checkIcon}
        </Stack>
      </Stack>
    );
  }, [formDataPayee, loadingWireInstitution]);

  /**
   * The function return the data (formData) and errors (formErrors)
   * - type: includes (person and business)
   * - accountType: includes (acc number and routing number)
   * When the use switch between type person and business,
   * we should reset the prev value: select person => reset business value or error
   */
  const formatDataWhenSwitchOptionPayee = useCallback(
    (fieldName, value) => {
      const formDataResult = { ...formDataPayee, [fieldName]: value };
      const formErrorsResult = { ...formErrorsPayee };

      switch (fieldName) {
        case 'type': // Use select type
          if (value === PAYEE_TYPES.PERSON) {
            // if the person => should reset business value
            return {
              data: { ...formDataResult, name: '' },
              errors: { ...formErrorsResult }
            };
          }
          return {
            // Otherwise should reset the person value (fName, lName)
            data: { ...formDataResult, firstName: '', lastName: '' },
            errors: { ...formErrorsResult }
          };
        case 'accountType': // When switching account type => should reset error and value (acc number and routing number)
          return {
            data: {
              ...formDataResult,
              accountNumber: '',
              confirmAccountNumber: ''
            },
            errors: {
              accountNumberError: null,
              confirmAccountNumberError: null,
              isAccountNumberMatched: false
            }
          };
        default:
          return { data: formDataResult, errors: formErrorsResult };
      }
    },
    [formDataPayee, formErrorsPayee]
  );

  const handleChangePayee = useCallback(
    (fieldName) => (event) => {
      const value = event.target.value ?? '';
      const isNumberInput = [
        FORM_FIELD_NAME.ACCOUNT_NUMBER,
        FORM_FIELD_NAME.ROUTING_NUMBER
      ].includes(fieldName);
      const { data, errors } = isNumberInput
        ? formatDataWhenSwitchOptionPayee(fieldName, value.trim())
        : formatDataWhenSwitchOptionPayee(fieldName, value);
      setFormDataPayee((prevData) => ({ ...prevData, ...data }));
      setFormErrorsPayee((preErrors) => ({ ...preErrors, ...errors }));
      setIsNotChangeFormDataPayee(false);
    },
    [formatDataWhenSwitchOptionPayee]
  );

  const handleBlurPayee = useCallback(
    (fieldName) => async () => {
      const { routingNumber, accountNumber, confirmAccountNumber } = formDataPayee;
      const { routingNumber: routingNumberUnit, accountNumber: accountNumberUnit } = unitAccount;
      const shouldTriggerValidation =
        previousRoutingNumber !== routingNumber || previousAccountNumber !== accountNumber;
      const existingInfoAccount =
        routingNumber === routingNumberUnit && accountNumber === accountNumberUnit;

      // When `onblur` on account number or confirm account number field
      // ==> should trigger action validate confirm
      const shouldValidateConfirmField = [
        FORM_FIELD_NAME.CONFIRM_ACCOUNT_NUMBER,
        FORM_FIELD_NAME.ACCOUNT_NUMBER
      ].includes(fieldName);
      const errorConfirmMessage = confirmRoutingValidator(accountNumber, confirmAccountNumber);
      if (shouldValidateConfirmField) {
        setFormErrorsPayee((errors) => ({
          ...errors,
          [FORM_FIELD_NAME.CONFIRM_ACCOUNT_NUMBER_ERROR]: confirmAccountNumber
            ? errorConfirmMessage
            : null,
          isAccountNumberMatched: Boolean(
            accountNumber && confirmAccountNumber && !errorConfirmMessage
          )
        }));
      }

      // validate for routingNumber and accountNumber
      if (shouldTriggerValidation) {
        const isAccountNumberField = fieldName === FORM_FIELD_NAME.ACCOUNT_NUMBER;
        const errorMessage = validateAccountErrorMessage(
          isAccountNumberField ? accountNumber : routingNumber,
          fieldName
        );

        if (existingInfoAccount) {
          setFormErrorsPayee((preErrors) => ({
            ...preErrors,
            routingNumberError: ROUTING_NUMBER_CURRENT_ERROR_MESSAGE
          }));
        } else {
          setFormErrorsPayee((errors) => ({
            ...errors,
            [isAccountNumberField
              ? FORM_FIELD_NAME.ACCOUNT_NUMBER_ERROR
              : FORM_FIELD_NAME.ROUTING_NUMBER_ERROR]: errorMessage
          }));
        }
        // verify routing number and display the institution
        const shouldCallAPIVerifyRoutingNumber =
          !errorMessage &&
          (fieldName === FORM_FIELD_NAME.ROUTING_NUMBER ||
            fieldName === FORM_FIELD_NAME.ACCOUNT_NUMBER) &&
          routingNumber &&
          routingNumber.length >= 9 &&
          !loadingInstitution;

        if (shouldCallAPIVerifyRoutingNumber) {
          // routing number
          const { data, error } = await getInstitution({
            variables: {
              params: {
                routingNumber: routingNumber.trim()
              }
            },
            skipCache: true
          });
          // Should show error in some cases
          // 1. API has error : error ==> show error: ROUTING_NUMBER_ERROR_MESSAGE
          // 2. API has data but data.institution is null or undefined :
          // 3. API has data isACHSupported: false is not supported
          // case 1 and case 2 show error : ROUTING_NUMBER_ERROR_MESSAGE
          // case 3 show error : BANK_NOT_SUPPORT_ACH
          const achIsNotSupported = data?.institution && !data?.institution?.isACHSupported;
          const shouldShowError = error || isUndefinedOrNull(data.institution) || achIsNotSupported;
          if (shouldShowError) {
            setFormDataPayee((prevData) => ({ ...prevData, institutionName: '' }));
            setFormErrorsPayee((preErrors) => ({
              ...preErrors,
              routingNumberError: achIsNotSupported
                ? BANK_NOT_SUPPORT_ACH_MESSAGE
                : ROUTING_NUMBER_ERROR_MESSAGE
            }));
          } else {
            setFormDataPayee((prevData) => ({
              ...prevData,
              institutionName: data?.institution?.name
            }));
            setFormErrorsPayee((preErrors) => ({
              ...preErrors,
              routingNumberError: existingInfoAccount ? ROUTING_NUMBER_CURRENT_ERROR_MESSAGE : null
            }));
          }
        }
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unitAccount, formDataPayee, loadingInstitution, previousRoutingNumber, previousAccountNumber]
  );

  const payeeTypeOptions = useMemo(() => {
    const { firstName, lastName, nickname, name, type } = formDataPayee;
    return [
      {
        value: PAYEE_TYPES.PERSON,
        label: (
          <Text
            size="md"
            weight="medium"
          >
            Person
          </Text>
        ),
        content: (
          <WrapContentStyled>
            {type === PAYEE_TYPES.PERSON && (
              <GroupTextInputStyled>
                <Stack gap="16px">
                  <Stack
                    direction={isDesktop ? 'row' : 'column'}
                    spacing="12px"
                  >
                    <TextField
                      fullWidth
                      inputProps={{
                        autoComplete: 'new-password'
                      }}
                      label="First Name"
                      onChange={handleChangePayee(FORM_FIELD_NAME.FIRST_NAME)}
                      value={firstName}
                    />
                    <TextField
                      fullWidth
                      inputProps={{
                        autoComplete: 'new-password'
                      }}
                      label="Last Name"
                      onChange={handleChangePayee(FORM_FIELD_NAME.LAST_NAME)}
                      value={lastName}
                    />
                  </Stack>
                  <Stack>
                    <TextField
                      inputProps={{
                        autoComplete: 'new-password'
                      }}
                      label="Payee Nickname (Optional)"
                      onChange={handleChangePayee(FORM_FIELD_NAME.NICK_NAME)}
                      value={nickname}
                    />
                  </Stack>
                </Stack>
              </GroupTextInputStyled>
            )}
          </WrapContentStyled>
        )
      },
      {
        value: PAYEE_TYPES.BUSINESS,
        label: (
          <Text
            size="md"
            weight="medium"
          >
            Business
          </Text>
        ),
        content: (
          <WrapContentStyled>
            {type === PAYEE_TYPES.BUSINESS && (
              <GroupTextInputStyled>
                <Stack direction="row">
                  <TextField
                    fullWidth
                    label="Business Name"
                    onChange={handleChangePayee(FORM_FIELD_NAME.NAME)}
                    value={name}
                  />
                </Stack>
              </GroupTextInputStyled>
            )}
          </WrapContentStyled>
        )
      }
    ];
  }, [formDataPayee, handleChangePayee, isDesktop]);

  const payeeAccountOptions = useMemo(() => {
    const { accountType, accountNumber, confirmAccountNumber } = formDataPayee;
    const {
      accountNumberError,
      routingNumberError,
      confirmAccountNumberError,
      isAccountNumberMatched
    } = formErrorsPayee;

    const accountInfo = (
      <GroupTextInputStyled>
        <Stack spacing="12px">
          <TextField
            disabled={loadingInstitution}
            error={Boolean(
              accountNumberError ||
                routingNumberError === ROUTING_NUMBER_CURRENT_ERROR_MESSAGE ||
                confirmAccountNumberError
            )}
            helperText={accountNumberError}
            InputProps={{
              endAdornment: isAccountNumberMatched && (
                <Box
                  aria-label="green check mark"
                  mr="8px"
                >
                  {validIcon}
                </Box>
              )
            }}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            inputProps={{
              autoComplete: 'new-password'
            }}
            label="Account Number"
            onBlur={handleBlurPayee(FORM_FIELD_NAME.ACCOUNT_NUMBER)}
            onChange={handleChangePayee(FORM_FIELD_NAME.ACCOUNT_NUMBER)}
            value={accountNumber}
          />
          <TextField
            error={Boolean(confirmAccountNumberError)}
            helperText={confirmAccountNumberError}
            InputProps={{
              endAdornment: isAccountNumberMatched && (
                <Box
                  aria-label="green check mark"
                  mr="8px"
                >
                  {validIcon}
                </Box>
              )
            }}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            inputProps={{
              autoComplete: 'new-password'
            }}
            label="Confirm Account Number"
            onBlur={handleBlurPayee(FORM_FIELD_NAME.CONFIRM_ACCOUNT_NUMBER)}
            onChange={handleChangePayee(FORM_FIELD_NAME.CONFIRM_ACCOUNT_NUMBER)}
            value={confirmAccountNumber}
          />
        </Stack>
      </GroupTextInputStyled>
    );

    return [
      {
        value: PAYEE_ACCOUNT_TYPES.CHECKING,
        label: (
          <Text
            size="md"
            weight="medium"
          >
            Checking
          </Text>
        ),
        content: (
          <WrapContentStyled>
            {accountType === PAYEE_ACCOUNT_TYPES.CHECKING && accountInfo}
          </WrapContentStyled>
        )
      },
      {
        value: PAYEE_ACCOUNT_TYPES.SAVING,
        label: (
          <Text
            size="md"
            weight="medium"
          >
            Savings
          </Text>
        ),
        content: (
          <WrapContentStyled>
            {accountType === PAYEE_ACCOUNT_TYPES.SAVING && accountInfo}
          </WrapContentStyled>
        )
      }
    ];
  }, [formDataPayee, formErrorsPayee, handleBlurPayee, handleChangePayee, loadingInstitution]);

  const payeeAccountRoutingNumberOptions = useMemo(() => {
    const { routingNumber, institutionName } = formDataPayee;
    const { routingNumberError } = formErrorsPayee;

    const accountInfo = (
      <GroupTextInputStyled>
        <Stack spacing="12px">
          <TextField
            error={Boolean(routingNumberError)}
            helperText={routingNumberError}
            inputProps={{
              maxLength: MAX_LENGTH_ROUTING_NUMBER,
              autoComplete: 'new-password'
            }}
            label="Routing Number"
            onBlur={handleBlurPayee(FORM_FIELD_NAME.ROUTING_NUMBER)}
            onChange={handleChangePayee(FORM_FIELD_NAME.ROUTING_NUMBER)}
            value={routingNumber}
          />
          <WrapInstitutionNameStyled direction="row">
            {!loadingInstitution &&
              institutionName &&
              (!routingNumberError ||
                routingNumberError === ROUTING_NUMBER_CURRENT_ERROR_MESSAGE) &&
              routingNumber.length > 0 && (
                <>
                  <Box mr="4px">
                    <Text
                      size="md"
                      weight="medium"
                    >
                      {institutionName}
                    </Text>
                  </Box>
                  {routingNumberError === ROUTING_NUMBER_CURRENT_ERROR_MESSAGE
                    ? warningIcon
                    : checkIcon}
                </>
              )}
            {loadingInstitution && (
              <CircularProgress
                color="secondary"
                size={20}
              />
            )}
          </WrapInstitutionNameStyled>
        </Stack>
      </GroupTextInputStyled>
    );

    return accountInfo;
  }, [formDataPayee, formErrorsPayee, handleBlurPayee, handleChangePayee, loadingInstitution]);

  const handleAfterEditPayeeSuccessfully = (counterParty) => {
    if (typeof editPayeeCallback === 'function') {
      editPayeeCallback();
    }

    // Determine the entry page from the current URL.
    const entryPage = getLastSegmentUrl(document.location.pathname);
    // we'll close the current modal and return to the entry page when current page is one of entry page
    if (entryPage && EDIT_PAYEE_ENTRY_PAGES.includes(entryPage)) {
      handleClose();
      return;
    }

    // Navigate to the new payee history page after editing
    navigate(
      generatePath(`${bankingPath}/payees/:counterPartyId`, {
        counterPartyId: counterParty.id
      }),
      { replace: true }
    );
    handleClose();
  };

  const hasWireInfoValid =
    formDataPayee.wireRoutingNumber &&
    !formErrorsPayee.wireRoutingNumberError &&
    Object.keys(wireAddress).length !== 0 &&
    !loadingWireInstitution;

  const handleSubmitPayee = async () => {
    const customerToken = await getCustomerToken();
    // bundle data
    const cloneFormData = trimObjValues({ ...formDataPayee });
    const {
      type,
      name,
      firstName,
      lastName,
      nickname,
      accountNumber,
      routingNumber,
      accountType,
      wireRoutingNumber
    } = cloneFormData;

    const { address: { addressLine1, city, state, zip, country } = {} } = wireAddress || {};

    const bundleData = {
      type,
      accountType,
      accountNumber,
      // if add payee via wire only, use wire routing number
      routingNumber: isSelectWire && !isSelectACH ? wireRoutingNumber : routingNumber,
      name: type === PAYEE_TYPES.BUSINESS ? name : [firstName, lastName].join(' '),
      ...(type === PAYEE_TYPES.PERSON && { firstName, lastName }),
      nickname,
      wire: hasWireInfoValid
        ? {
            // wire routing number is optional, should be set if add payee via wire + ach
            routingNumber: isSelectWire && isSelectACH ? wireRoutingNumber : undefined,
            address: {
              street: addressLine1,
              city,
              state,
              postalCode: zip,
              country
            }
          }
        : undefined
    };

    const { data, error: editPayeeError } = await editPayee({
      variables: {
        input: {
          attributes: {
            ...bundleData
          },
          bankingAccountId,
          counterpartyId: counterPartyId,
          idempotencyKey,
          customerToken
        }
      },
      skip: loadingEditPayee
    });

    if (editPayeeError) {
      createErrorToast({
        title: 'An error occurred, please try again',
        error: editPayeeError
      });
      return;
    }

    createSuccessToast({
      title: `${bundleData?.name ?? ''} is updated`
    });

    const counterParty = data.updateBankingPayeeCounterparty;
    handleAfterEditPayeeSuccessfully(counterParty);
  };

  /**
   * validate for Payee type (person/business)
   */
  const isValidPayeeType = useMemo(() => {
    if (
      (formDataPayee.type === PAYEE_TYPES.PERSON &&
        checkValidFields(formDataPayee, [
          FORM_FIELD_NAME.TYPE,
          FORM_FIELD_NAME.FIRST_NAME,
          FORM_FIELD_NAME.LAST_NAME
        ])) ||
      (formDataPayee.type === PAYEE_TYPES.BUSINESS &&
        checkValidFields(formDataPayee, [FORM_FIELD_NAME.TYPE, FORM_FIELD_NAME.NAME]))
    ) {
      return true;
    }

    return false;
  }, [formDataPayee]);

  /**
   * validate Payee account fulfill data, return TRUE when fulfill data
   */
  const isValidPayeeAccount = useMemo(() => {
    const fieldsNeedValid = isSelectACH
      ? [
          FORM_FIELD_NAME.ACCOUNT_TYPE,
          FORM_FIELD_NAME.ACCOUNT_NUMBER,
          FORM_FIELD_NAME.ROUTING_NUMBER,
          FORM_FIELD_NAME.CONFIRM_ACCOUNT_NUMBER
        ]
      : [
          FORM_FIELD_NAME.ACCOUNT_TYPE,
          FORM_FIELD_NAME.ACCOUNT_NUMBER,
          FORM_FIELD_NAME.WIRE_ROUTING_NUMBER,
          FORM_FIELD_NAME.WIRE_INSTITUTION_NAME,
          FORM_FIELD_NAME.CONFIRM_ACCOUNT_NUMBER
        ];

    return checkValidFields(formDataPayee, fieldsNeedValid);
  }, [formDataPayee, isSelectACH]);

  /**
   * validate account format, return TRUE when no any error
   */
  const isValidAccountFormat = useMemo(() => {
    const fieldsNeedValid = isSelectACH
      ? [
          FORM_FIELD_NAME.ACCOUNT_NUMBER_ERROR,
          FORM_FIELD_NAME.ROUTING_NUMBER_ERROR,
          FORM_FIELD_NAME.CONFIRM_ACCOUNT_NUMBER_ERROR
        ]
      : [
          FORM_FIELD_NAME.ACCOUNT_NUMBER_ERROR,
          FORM_FIELD_NAME.WIRE_ROUTING_NUMBER_ERROR,
          FORM_FIELD_NAME.CONFIRM_ACCOUNT_NUMBER_ERROR
        ];

    return (
      fieldsNeedValid.every((fieldName) => formErrorsPayee[fieldName] === null) &&
      formErrorsPayee.isAccountNumberMatched
    );
  }, [formErrorsPayee, isSelectACH]);

  /**
   * Check valid wire info
   * if input wire info is false => is valid
   * if input wire info is true => will check data is valid
   *
   * @returns {boolean} data valid wire info
   */
  const isValidWireInfo = () => {
    const hasInputWireInfo =
      formDataPayee.wireRoutingNumber.length > 0 || wireAddressInput.length > 0;

    if (!hasInputWireInfo) {
      return true;
    }

    const isValidData =
      !formErrorsPayee.wireRoutingNumberError &&
      wireAddress.valid &&
      formDataPayee.wireRoutingNumber.length > 0 &&
      Object.keys(wireAddress).length !== 0 &&
      formDataPayee.wireInstitutionName;

    return isValidData;
  };

  const isDisableSubmitPayee =
    loadingEditPayee ||
    (isSelectACH && !formDataPayee.institutionName) ||
    (isSelectWire && !formDataPayee.wireInstitutionName) ||
    !(
      isValidPayeeType &&
      isValidPayeeAccount &&
      isValidAccountFormat &&
      !loadingInstitution &&
      !loadingWireInstitution &&
      !loadingLimit &&
      !isFetchingBankingAccounts
    ) ||
    !isValidWireInfo();

  const handleSelectWire = () => {
    setSelectWire((isSelect) => !isSelect);
    setFormDataPayee((prevData) => ({
      ...prevData,
      wireRoutingNumber: '',
      wireInstitutionName: ''
    }));
    setWireAddressInput('');
    setWireAddress({});
    setFormErrorsPayee((prevErrors) => ({ ...prevErrors, wireRoutingNumberError: null }));
    setIsNotChangeFormDataPayee(false);
  };

  const handleSelectACH = () => {
    setSelectACH((isSelect) => !isSelect);
    setFormDataPayee((prevData) => ({ ...prevData, routingNumber: '', institutionName: '' }));
    setFormErrorsPayee((prevErrors) => ({
      ...prevErrors,
      routingNumberError: null,
      institutionName: null
    }));
    setIsNotChangeFormDataPayee(false);
  };

  const wireAddressDefaultValue = useMemo(() => {
    const { street, city, state } = counterPartyInfo?.wire?.address || {};
    return `${street || ''}, ${city || ''}, ${state || ''}, USA`;
  }, [counterPartyInfo]);

  const handleDeleteSuccess = () => {
    deletePayeeCallback();
    setTimeout(() => {
      handleClose();
    }, 1000);
  };

  const handleConfirmDeletePayee = () => {
    openModal({
      component: ConfirmDeletePayeeModal,
      id: counterPartyId,
      firstName: counterPartyName,
      handleSuccess: handleDeleteSuccess
    });
  };

  return (
    <>
      {/** Payee type */}
      <FieldContainerStyled>
        <Stack spacing="48px">
          <Stack spacing={{ xs: '16px', md: '24px' }}>
            <Stack spacing="8px">
              <Text
                size="lg"
                weight="medium"
              >
                Payee Name
              </Text>
            </Stack>
            <RadioGroup
              aria-label="Choose payee type."
              name="payee-type-radio-buttons"
              onChange={handleChangePayee(FORM_FIELD_NAME.TYPE)}
              value={formDataPayee.type}
            >
              {payeeTypeOptions.map(({ value, label, content }) => (
                <Box key={value}>
                  <FormControlLabel
                    control={<Radio />}
                    label={label}
                    value={value}
                  />
                  {content}
                </Box>
              ))}
            </RadioGroup>
          </Stack>
          {/** Payee account */}
          <Stack spacing={{ xs: '16px', md: '24px' }}>
            <Stack spacing="8px">
              <Text
                size="lg"
                weight="medium"
              >
                Account Type
              </Text>
            </Stack>
            <RadioGroup
              aria-label="Choose payee account."
              name="payee-account-radio-buttons"
              onChange={handleChangePayee(FORM_FIELD_NAME.ACCOUNT_TYPE)}
              value={formDataPayee.accountType}
            >
              {payeeAccountOptions.map(({ value, label, content }) => (
                <Box key={value}>
                  <FormControlLabel
                    control={<Radio />}
                    label={label}
                    value={value}
                  />
                  {content}
                </Box>
              ))}
            </RadioGroup>
          </Stack>

          <Stack spacing="16px">
            <Stack spacing="8px">
              <Text
                size="lg"
                weight="medium"
              >
                Transfer Type
              </Text>
            </Stack>
            <Stack gap="24px">
              <Stack gap="16px">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isSelectACH}
                      id="ach"
                      onChange={handleSelectACH}
                    />
                  }
                  label={
                    <Stack>
                      <Text
                        size="md"
                        weight="medium"
                      >
                        Bank transfer (ACH)
                      </Text>
                      <Text
                        color="tertiary"
                        size="md"
                      >
                        No fees. May take up to 3 days to process
                      </Text>
                    </Stack>
                  }
                />
                <WrapContentStyled>
                  {isSelectACH && payeeAccountRoutingNumberOptions}
                </WrapContentStyled>
              </Stack>
              <Stack gap="16px">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isSelectWire}
                      id="wire"
                      onChange={handleSelectWire}
                    />
                  }
                  label={
                    <Stack>
                      <Text
                        size="md"
                        weight="medium"
                      >
                        Wire transfers{' '}
                      </Text>
                      <Text
                        color="tertiary"
                        size="md"
                      >
                        {`${wireOutgoingFeeText} per transfer. Same day transfer`}
                      </Text>
                    </Stack>
                  }
                />
                <WrapContentStyled>
                  {isSelectWire && (
                    <Stack gap="16px">
                      <TextField
                        disabled={loadingEditPayee}
                        error={Boolean(wireRoutingNumberError)}
                        helperText={wireRoutingNumberError}
                        inputProps={{
                          autoComplete: 'new-password',
                          maxLength: 9,
                          'aria-label': 'Wire routing number field'
                        }}
                        label="Wire-specific Routing Number"
                        onBlur={handleBlurWireRoutingNumber}
                        onChange={handleChangeWireRoutingNumber}
                        value={formDataPayee.wireRoutingNumber}
                      />
                      {inputInfoRender()}
                      <GoogleMapField
                        dataChangeHandler={setWireAddress}
                        // default value should empty when wire routing number clear
                        defaultValue={
                          isWireSupported && formDataPayee.wireRoutingNumber
                            ? wireAddressDefaultValue
                            : ''
                        }
                        disableClearable
                        disabled={loadingEditPayee}
                        errorMessage="Please enter a valid address"
                        onChangeInput={setWireAddressInput}
                        placeholder="Address on file at bank"
                        sizeError="body_smallRegular"
                        validateEmpty={false}
                      />
                    </Stack>
                  )}
                </WrapContentStyled>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </FieldContainerStyled>

      <LoadingButton
        color="error"
        disabled={loadingEditPayee}
        fullWidth
        onClick={handleConfirmDeletePayee}
        size="large"
        variant="outlined"
      >
        Delete payee
      </LoadingButton>

      <ActionButtonsGrid
        sx={{
          zIndex: 9
        }}
      >
        <LoadingButton
          color="primary"
          disabled={isNotChangeFormDataPayee || isDisableSubmitPayee}
          loading={loadingEditPayee}
          onClick={handleSubmitPayee}
          size="large"
          variant="contained"
        >
          Update
        </LoadingButton>
      </ActionButtonsGrid>
    </>
  );
}

export default EditPayee;

EditPayee.propTypes = {
  handleClose: PropTypes.func.isRequired,
  editPayeeCallback: PropTypes.func,
  deletePayeeCallback: PropTypes.func,
  counterPartyInfo: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    bank: PropTypes.string,
    nickname: PropTypes.string,
    accountNumber: PropTypes.string,
    accountType: PropTypes.string,
    fullAccountNumber: PropTypes.string,
    fullRoutingNumber: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    wire: PropTypes.shape({
      address: PropTypes.shape({
        city: PropTypes.string,
        country: PropTypes.string,
        postalCode: PropTypes.string,
        state: PropTypes.string,
        street: PropTypes.string,
        street2: PropTypes.string
      }),
      routingNumber: PropTypes.string
    }),
    type: PropTypes.string,
    routingNumber: PropTypes.string,
    isWireSupported: PropTypes.bool,
    isACHSupported: PropTypes.bool
  })
};
